import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Info, Loader2, Printer, TriangleAlert, ZoomIn, ZoomOut, Download } from 'lucide-react';
import type { CaixaMovel, ConfigPlanner, DocumentoMiolo, Pagina, Paleta } from '@miolos/core';
import { PAPEIS, QUADROS_DIA, posicoesLivresPermitidas } from '@miolos/core';
import { urlDaImagem } from '@/lib/api';
import { useAcesso } from '@/auth/EstadoAcesso';
import { MolduraPrevia } from '@/impressao/MolduraPrevia';
import { PaginaImpressa, medidasDaPagina } from '@/impressao/PaginaImpressa';
import { AjusteDeCaixas } from './AjusteDeCaixas';
import { PosicionadorDeCaixas } from './PosicionadorDeCaixas';
import type { MapaDePosicoes } from '@/lib/posicoes-livres';
import { Badge, Separator } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ROTULO_TIPO: Record<Pagina['tipo'], string> = {
  CAPA: 'Capa',
  CONTRACAPA: 'Contracapa',
  DADOS_PESSOAIS: 'Meus dados',
  ANO: 'Visão do ano',
  MES: 'Divisória de mês',
  DIA: 'Dia',
  NOTAS: 'Anotações',
  ANIVERSARIOS: 'Aniversários',
  CONTATOS: 'Contatos',
  METAS: 'Metas',
  INDICE: 'Índice',
  CADERNO: 'Caderno',
  DIVISORIA: 'Divisória',
};

interface Props {
  config: ConfigPlanner;
  paleta: Paleta;
  documento: DocumentoMiolo;
  aoImprimir: () => void;
  imprimindo: boolean;
  rotuloBotao?: string;
  /**
   * Baixar o PDF desenhado no servidor. Sem esta função o botão não aparece —
   * é o caso do assistente antes de o miolo ser salvo, quando ainda não há o que
   * o servidor possa buscar.
   */
  aoBaixarPdf?: () => void;
  /** O servidor está desenhando o arquivo — o clique já voltou, o desenho não. */
  gerandoPdf?: boolean;
  /** Recado da fila: "PDF pronto", ou o motivo de ter falhado. */
  avisoPdf?: string | null;
  /**
   * Guarda as posições escolhidas à mão. Sem esta função o ajuste não aparece:
   * é o caso do miolo aberto só para ver, sem editar.
   */
  aoMudarPosicoes?: (posicoes: MapaDePosicoes) => void;
}

export function PainelPrevia({
  config,
  paleta,
  documento,
  aoImprimir,
  imprimindo,
  rotuloBotao = 'Gerar PDF / imprimir',
  aoBaixarPdf,
  gerandoPdf = false,
  avisoPdf = null,
  aoMudarPosicoes,
}: Props) {
  const [indice, setIndice] = useState(0);
  /** Zoom da prévia: 1 = a página cabendo na largura disponível. */
  const [zoom, setZoom] = useState(1);
  /** O ajuste das caixas está ligado? */
  const [posicionando, setPosicionando] = useState(false);
  const { marcaDagua, textoMarcaDagua, detalheMarcaDagua } = useAcesso();

  const paginas = documento.paginas;
  const total = paginas.length;

  // Mantém o índice válido quando a configuração muda (ex.: menos páginas).
  useEffect(() => {
    setIndice((atual) => Math.min(atual, Math.max(0, total - 1)));
  }, [total]);

  const pagina = paginas[Math.min(indice, Math.max(0, total - 1))];

  const grupos = useMemo(() => {
    const mapa = new Map<Pagina['tipo'], Pagina[]>();
    for (const item of paginas) {
      const lista = mapa.get(item.tipo) ?? [];
      lista.push(item);
      mapa.set(item.tipo, lista);
    }
    return mapa;
  }, [paginas]);

  if (!pagina) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
        Nenhuma página gerada com a configuração atual. Revise o período escolhido.
      </div>
    );
  }

  /*
   * O rótulo do tamanho sai do papel, e não de uma lista fixa: papel novo no
   * catálogo passa a aparecer aqui sozinho.
   */
  const proporcao = `${config.formato} · ${PAPEIS[config.formato].larguraMm}×${PAPEIS[config.formato].alturaMm}mm`;

  /*
   * O ajuste das caixas só existe onde as caixas podem ser movidas: uma página de
   * dia com a folha inteira para ela. As alças usam a mesma régua da página
   * desenhada abaixo — régua diferente e a caixa dançaria em relação ao desenho.
   */
  const lado = indice % 2 === 0 ? 'ESQUERDO' : 'DIREITO';
  const medidas = medidasDaPagina(config, pagina.formato, lado);
  const podePosicionar = Boolean(aoMudarPosicoes) && pagina.tipo === 'DIA' && posicoesLivresPermitidas(config);
  const chavesMoveis: CaixaMovel[] = [
    'ESCRITA',
    ...QUADROS_DIA.filter((quadro) => config.quadros[quadro]),
  ];
  const ajusteLigado = posicionando && podePosicionar;

  return (
    <div className="flex flex-col gap-4">
      {/* Navegação entre páginas */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="contorno"
          tamanho="icone"
          onClick={() => setIndice((atual) => Math.max(0, atual - 1))}
          disabled={indice === 0}
          aria-label="Página anterior"
        >
          <ChevronLeft />
        </Button>
        <div className="min-w-0 flex-1">
          <Select
            value={String(indice)}
            onValueChange={(valor) => setIndice(Number(valor))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Escolha uma página" />
            </SelectTrigger>
            <SelectContent>
              {Array.from(grupos.entries()).flatMap(([tipo, itens]) => [
                <SelectItem key={`grupo-${tipo}`} value={`grupo-${tipo}`} disabled>
                  {ROTULO_TIPO[tipo]} ({itens.length})
                </SelectItem>,
                ...itens.slice(0, 40).map((item) => {
                  const posicao = paginas.indexOf(item);
                  return (
                    <SelectItem key={`${item.id}-${posicao}`} value={String(posicao)}>
                      {`pág. ${posicao + 1} · ${item.titulo}`}
                    </SelectItem>
                  );
                }),
              ])}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="contorno"
          tamanho="icone"
          onClick={() => setIndice((atual) => Math.min(total - 1, atual + 1))}
          disabled={indice >= total - 1}
          aria-label="Próxima página"
        >
          <ChevronRight />
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          Página <strong className="text-foreground">{indice + 1}</strong> de {total}
        </span>
        <span>{proporcao}</span>
      </div>

      {/*
       * Zoom da prévia.
       *
       * Numa tela de celular a página inteira encolhe até o texto virar borrão;
       * sem estes botões não há como ler o que vai ser impresso. Ampliando, a
       * página fica maior que a área e o container abaixo passa a rolar.
       */}
      <div className="flex items-center justify-end gap-1">
        <Button
          variant="fantasma"
          tamanho="icone"
          onClick={() => setZoom((atual) => Math.max(1, Number((atual - 0.25).toFixed(2))))}
          disabled={zoom <= 1}
          aria-label="Diminuir o zoom da prévia"
        >
          <ZoomOut />
        </Button>
        <span className="min-w-12 text-center font-mono text-xs text-muted-foreground">
          {Math.round(zoom * 100)}%
        </span>
        <Button
          variant="fantasma"
          tamanho="icone"
          onClick={() => setZoom((atual) => Math.min(4, Number((atual + 0.25).toFixed(2))))}
          disabled={zoom >= 4}
          aria-label="Aumentar o zoom da prévia"
        >
          <ZoomIn />
        </Button>
      </div>

      {/* A página, em tamanho real escalado */}
      <div className={zoom > 1 ? 'overflow-auto rounded-md border border-border' : undefined}>
        <MolduraPrevia formato={config.formato} papel={config.papel} fatorZoom={zoom} sombra={zoom <= 1}>
          <PaginaImpressa
            pagina={pagina}
            paleta={paleta}
            config={config}
            lado={lado}
            resolverImagem={urlDaImagem}
            marcaDagua={marcaDagua ? textoMarcaDagua : null}
            marcaDaguaDetalhe={detalheMarcaDagua}
          />
          {ajusteLigado && aoMudarPosicoes ? (
            <PosicionadorDeCaixas
              config={config}
              medidas={medidas}
              chaves={chavesMoveis}
              aoMudar={aoMudarPosicoes}
            />
          ) : null}
        </MolduraPrevia>
      </div>

      <Separator />

      {/* Posicionar as caixas do dia: arrasto na prévia ou milímetros no campo */}
      {aoMudarPosicoes ? (
        pagina.tipo === 'DIA' && posicoesLivresPermitidas(config) ? (
          <AjusteDeCaixas
            config={config}
            medidas={medidas}
            chaves={chavesMoveis}
            ligado={ajusteLigado}
            aoLigar={setPosicionando}
            aoMudar={aoMudarPosicoes}
          />
        ) : (
          <p className="flex items-start gap-2 rounded-md border border-border bg-muted/60 p-2.5 text-xs leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Escolha uma <strong className="font-semibold text-foreground">página de dia</strong> para posicionar as
              caixas à mão
              {pagina.tipo === 'DIA' ? ' — com dois dias na mesma folha a página é repartida entre eles.' : '.'}
            </span>
          </p>
        )
      ) : null}

      {/* Resumo técnico do miolo */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <Item rotulo="Páginas totais" valor={String(documento.resumo.totalPaginas)} />
        <Item rotulo="Dias gerados" valor={String(documento.resumo.paginasDeDia)} />
        <Item rotulo="Área de escrita" valor={`${documento.resumo.alturaEscritaMm} mm por dia`} />
        <Item rotulo="Contraste do texto" valor={`${documento.resumo.contrasteTexto}:1`} />
        <Item rotulo="Harmonia aplicada" valor={paleta.esquemaAplicado.replace(/_/g, ' ').toLowerCase()} />
        <Item rotulo="Cadernos" valor={`${documento.resumo.cadernos} (${documento.resumo.paginasDeCaderno} págs)`} />
        {documento.resumo.anosDaVisao && documento.resumo.anosDaVisao.length > 1 ? (
          <Item rotulo="Anos na agenda" valor={documento.resumo.anosDaVisao.join(' · ')} />
        ) : null}
      </dl>

      {documento.avisos.length > 0 ? (
        <ul className="space-y-2">
          {documento.avisos.map((aviso, i) => (
            <li
              key={i}
              className={[
                'flex gap-2 rounded-md border p-2.5 text-xs leading-relaxed',
                aviso.nivel === 'ATENCAO'
                  ? 'border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
                  : 'border-border bg-muted/60 text-muted-foreground',
              ].join(' ')}
            >
              {aviso.nivel === 'ATENCAO' ? (
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              ) : (
                <Info className="mt-0.5 size-3.5 shrink-0" />
              )}
              <span>{aviso.mensagem}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={aoImprimir} disabled={imprimindo} className="flex-1">
          <Printer />
          {imprimindo ? 'Preparando...' : rotuloBotao}
        </Button>
        {aoBaixarPdf ? (
          <Button
            variant="contorno"
            onClick={aoBaixarPdf}
            title="O servidor desenha o PDF e guarda o arquivo para baixar de novo depois. Pode continuar navegando enquanto ele é desenhado."
          >
            {gerandoPdf ? <Loader2 className="animate-spin" /> : <Download />}
            {gerandoPdf ? 'Gerando o PDF...' : 'Baixar PDF'}
          </Button>
        ) : null}
        <Badge tom="principal">{total} páginas</Badge>
      </div>
      {gerandoPdf ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          O arquivo está sendo desenhado no servidor. Pode continuar aqui ou sair desta página — quando
          ficar pronto, o download começa sozinho.
        </p>
      ) : null}
      {avisoPdf ? (
        <p className="text-xs leading-relaxed text-muted-foreground">{avisoPdf}</p>
      ) : null}
      <p className="text-xs leading-relaxed text-muted-foreground">
        {aoBaixarPdf
          ? 'O arquivo do servidor sai no tamanho exato da folha, igual para todo mundo. No botão de imprimir, a janela do navegador pede Salvar como PDF, papel '
          : 'Na janela de impressão escolha '}
        {aoBaixarPdf ? <>{config.formato} e margens <strong>nenhuma</strong>.</> : <><strong>Salvar como PDF</strong>, papel {config.formato} e margens <strong>nenhuma</strong> para sair no tamanho exato.</>}
      </p>
    </div>
  );
}

function Item({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{rotulo}</dt>
      <dd className="font-medium capitalize text-foreground">{valor}</dd>
    </div>
  );
}
